import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material'
import { interventionConfigsAPI, contextRulesAPI, riskCodesAPI, assessmentInstrumentsAPI } from '../../services/api'
import ContextRuleEditorDialog from './ContextRuleEditorDialog'

// Enabled/disabled indicator
const enabledStyles = {
  true: { color: '#16a34a', label: 'Enabled' },
  false: { color: '#71717a', label: 'Disabled' },
}

function EnabledIndicator({ enabled, onClick, clickable }) {
  const style = enabledStyles[String(enabled)]
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        cursor: clickable ? 'pointer' : 'default',
        '&:hover': clickable ? { opacity: 0.8 } : {},
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.color,
        }}
      />
      <Typography variant="caption" sx={{ color: style.color, fontSize: '0.75rem' }}>
        {style.label}
      </Typography>
    </Box>
  )
}

/**
 * Generate a human-readable summary of a context rule
 */
function getRuleSummary(ruleDefinition) {
  const conditions = ruleDefinition?.conditions || []
  const actions = ruleDefinition?.actions || []

  if (conditions.length === 0 && actions.length === 0) {
    return 'No conditions or actions defined'
  }

  const conditionSummary = conditions.map((c) => {
    if (c.type === 'assessment_score') {
      return `${c.instrument_code} ${c.operator?.replace(/_/g, ' ')} ${c.value}`
    }
    if (c.type === 'assessment_trend') {
      return `Last ${c.count} ${c.instrument_code} scores ${c.operator?.replace(/_/g, ' ')} ${c.value}`
    }
    return c.type
  })

  const actionSummary = actions.map((a) => {
    if (a.type === 'set_risk_factor') {
      const v = a.value ?? a.severity
      return `Set ${a.risk_code}${v != null && v !== '' ? ` = ${v}` : ''}`
    }
    if (a.type === 'clear_risk_factor') {
      return `Clear ${a.risk_code} risk`
    }
    return a.type
  })

  const matchMode = ruleDefinition?.match_mode === 'any' ? ' OR ' : ' AND '
  return `When ${conditionSummary.join(matchMode) || 'always'}: ${actionSummary.join(', ')}`
}

function ContextRulesTab({ interventionId, canEdit, onError }) {
  const [attachedRules, setAttachedRules] = useState([])
  const [interventionRiskCodes, setInterventionRiskCodes] = useState([])
  const [allRiskCodes, setAllRiskCodes] = useState([])
  const [allAssessments, setAllAssessments] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingRule, setEditingRule] = useState(null)

  // Drag-to-reorder state
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [dropTargetIndex, setDropTargetIndex] = useState(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const [attached, ivRiskCodes, allRc, assessments] = await Promise.all([
        interventionConfigsAPI.getContextRules(interventionId),
        interventionConfigsAPI.getRiskCodes(interventionId).catch(() => []),
        riskCodesAPI.getAll().then(res => res.data || []).catch(() => []),
        assessmentInstrumentsAPI.getAll(true).catch(() => []),
      ])
      setAttachedRules(attached)
      setInterventionRiskCodes(ivRiskCodes || [])
      setAllRiskCodes(allRc || [])
      setAllAssessments(assessments || [])
    } catch (err) {
      console.error('Error fetching patient rules:', err)
      onError?.('Failed to load patient rules')
    } finally {
      setLoading(false)
    }
  }, [interventionId, onError])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleOpenEditor = async (rule = null) => {
    // Refresh the intervention's attached risk codes right before opening the
    // editor. RiskCodesTab and this tab are mounted as siblings, so a code
    // attached/removed in the Risk Codes section doesn't propagate here on its
    // own. Pulling fresh data on open ensures the editor's risk-code dropdown
    // reflects the current attachment set.
    try {
      const fresh = await interventionConfigsAPI.getRiskCodes(interventionId)
      setInterventionRiskCodes(fresh || [])
    } catch (err) {
      console.error('Failed to refresh intervention risk codes:', err)
    }
    setEditingRule(rule)
    setEditorOpen(true)
  }

  const handleCloseEditor = () => {
    setEditorOpen(false)
    setEditingRule(null)
  }

  const handleSaveRule = async (ruleData) => {
    try {
      setSaving(true)
      if (editingRule) {
        await contextRulesAPI.update(editingRule.id, ruleData)
      } else {
        const newRule = await contextRulesAPI.create(ruleData)
        const ruleId = newRule.data?.id || newRule.id
        await interventionConfigsAPI.addContextRule(interventionId, {
          context_rule_id: ruleId,
          is_enabled: true,
        })
      }
      await fetchData()
      handleCloseEditor()
    } catch (err) {
      console.error('Error saving patient rule:', err)
      throw err
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveRule = async (ruleId) => {
    if (!window.confirm('Remove this patient rule from the intervention?')) return

    try {
      setSaving(true)
      await interventionConfigsAPI.removeContextRule(interventionId, ruleId)
      setAttachedRules(attachedRules.filter((r) => r.id !== ruleId))
    } catch (err) {
      console.error('Error removing patient rule:', err)
      onError?.('Failed to remove patient rule')
    } finally {
      setSaving(false)
    }
  }

  const persistOrder = async (orderedList) => {
    for (let i = 0; i < orderedList.length; i++) {
      const r = orderedList[i]
      await interventionConfigsAPI.addContextRule(interventionId, {
        context_rule_id: r.id,
        is_enabled: r.is_enabled,
        priority_order: i,
      })
    }
  }

  const handleDragStart = (e, index) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    try { e.dataTransfer.setData('text/plain', String(index)) } catch (_) { /* noop */ }
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (draggedIndex !== null && dropTargetIndex !== index) {
      setDropTargetIndex(index)
    }
  }

  const handleDrop = async (e) => {
    e.preventDefault()
    if (draggedIndex === null || dropTargetIndex === null || draggedIndex === dropTargetIndex) {
      setDraggedIndex(null)
      setDropTargetIndex(null)
      return
    }
    const reordered = [...attachedRules]
    const [moved] = reordered.splice(draggedIndex, 1)
    reordered.splice(dropTargetIndex, 0, moved)
    setAttachedRules(reordered.map((r, i) => ({ ...r, priority_order: i })))
    setDraggedIndex(null)
    setDropTargetIndex(null)
    try {
      setSaving(true)
      await persistOrder(reordered)
    } catch (err) {
      console.error('Error reordering patient rules:', err)
      onError?.('Failed to save new order')
      await fetchData()
    } finally {
      setSaving(false)
    }
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
    setDropTargetIndex(null)
  }

  const handleToggleEnabled = async (rule) => {
    try {
      setSaving(true)
      await interventionConfigsAPI.addContextRule(interventionId, {
        context_rule_id: rule.id,
        is_enabled: !rule.is_enabled,
        priority_order: rule.priority_order,
      })
      await fetchData()
    } catch (err) {
      console.error('Error updating patient rule:', err)
      onError?.('Failed to update patient rule')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {attachedRules.length} rule{attachedRules.length !== 1 ? 's' : ''} attached
          </Typography>
          {canEdit && attachedRules.length > 1 && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6875rem' }}>
              Drag to reorder — lower position evaluates first
            </Typography>
          )}
        </Box>
        {canEdit && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => handleOpenEditor()}>
            Add Rule
          </Button>
        )}
      </Box>

      {attachedRules.length === 0 ? (
        <Alert severity="info">
          No patient rules attached. Add rules to automatically update patient risk factors and context properties.
        </Alert>
      ) : (
        <Paper variant="outlined">
          <List disablePadding>
            {attachedRules.map((rule, index) => {
              const isDragging = draggedIndex === index
              const isDropTarget = dropTargetIndex === index && draggedIndex !== null && draggedIndex !== index
              return (
                <ListItem
                  key={rule.id}
                  divider={index < attachedRules.length - 1}
                  draggable={canEdit}
                  onDragStart={canEdit ? (e) => handleDragStart(e, index) : undefined}
                  onDragOver={canEdit ? (e) => handleDragOver(e, index) : undefined}
                  onDrop={canEdit ? handleDrop : undefined}
                  onDragEnd={canEdit ? handleDragEnd : undefined}
                  sx={{
                    py: 1,
                    px: 2,
                    opacity: isDragging ? 0.4 : (rule.is_enabled ? 1 : 0.6),
                    borderTop: isDropTarget && draggedIndex > index ? '2px solid' : '2px solid transparent',
                    borderBottom: isDropTarget && draggedIndex < index ? '2px solid' : undefined,
                    borderTopColor: isDropTarget && draggedIndex > index ? 'primary.main' : 'transparent',
                    borderBottomColor: isDropTarget && draggedIndex < index ? 'primary.main' : undefined,
                    transition: 'border-color 0.12s ease',
                  }}
                >
                  {canEdit && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'text.disabled',
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' },
                        mr: 1,
                        ml: -0.5,
                      }}
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </Box>
                  )}
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500}>
                        {getRuleSummary(rule.rule_definition)}
                      </Typography>
                    }
                    secondary={
                      <Box component="span" display="flex" gap={2} mt={0.25}>
                        <Typography variant="caption" color="text.secondary" component="span">
                          Match: {rule.rule_definition?.match_mode || 'all'}
                        </Typography>
                      </Box>
                    }
                  />
                  <Box display="flex" alignItems="center" gap={1}>
                    <EnabledIndicator
                      enabled={rule.is_enabled}
                      onClick={() => canEdit && handleToggleEnabled(rule)}
                      clickable={canEdit}
                    />
                    {canEdit && (
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => handleOpenEditor(rule)}
                          disabled={saving}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canEdit && (
                      <Tooltip title="Remove">
                        <IconButton
                          onClick={() => handleRemoveRule(rule.id)}
                          disabled={saving}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </ListItem>
              )
            })}
          </List>
        </Paper>
      )}

      <ContextRuleEditorDialog
        open={editorOpen}
        onClose={handleCloseEditor}
        rule={editingRule}
        onSave={handleSaveRule}
        saving={saving}
        interventionRiskCodes={interventionRiskCodes}
        allRiskCodes={allRiskCodes}
        allAssessments={allAssessments}
      />
    </Box>
  )
}

export default ContextRulesTab
